import type { PalettesProps } from "@ant-design/colors";
import type { PaletteColorOptions } from "@mui/material/styles";

import type { PaletteThemeProps } from "types/theme";

// ==============================|| PRESET THEME - TOBY (MANTIS BRANDED) ||============================== //

export default function Toby(colors: PalettesProps): PaletteThemeProps {
  const { grey } = colors;
  const greyColors: PaletteColorOptions = {
    0: grey[0],
    50: grey[1],
    100: grey[2],
    200: grey[3],
    300: grey[4],
    400: grey[5],
    500: grey[6],
    600: grey[7],
    700: grey[8],
    800: grey[9],
    900: grey[10],
    A50: grey[15],
    A100: grey[11],
    A200: grey[12],
    A400: grey[13],
    A700: grey[14],
    A800: grey[16],
  };
  const contrastText = "#fff";

  // Primary scale, lightest → darkest. Mantis defines `Primary/1..8`; two
  // Figma-driven slots (`50` and `800`) are interleaved at their natural
  // positions on the scale. The `900` slot mirrors `darker` to satisfy MUI's
  // 10-stop shape.
  const primaryColors = [
    "#eaf0ff", // Figma Secondary/50 → 50 (chip-background tint)
    "#e4e9f9", // Primary/1 → lighter
    "#e4e9f9", // Primary/1 → 100 (duplicate, mirroring theme convention)
    "#c8d3f3", // Primary/2 → 200
    "#9fafe2", // Primary/3 → light
    "#607ac8", // Primary/4 → 400
    "#0d32a5", // Primary/5 → main
    "#0a2884", // Primary/6 → dark (hover)
    "#081e63", // Primary/7 → 700
    "#14265e", // Figma Secondary/700 → 800 (nav-dark, instruction-step circle)
    "#051442", // Primary/8 → darker
    "#051442", // Primary/8 → 900 (mirror)
  ];

  const errorColors = ["#fff1f0", "#ffccc7", "#ff4d4f", "#cf1322", "#5c0011"];
  const warningColors = ["#fffbe6", "#ffe58f", "#faad14", "#ad6800", "#613400"];

  const infoColors = [
    "#f3feff", // Info/50  → lighter
    "#d7f5f9", // Info/100
    "#aeebf2", // Info/200 → light  (pill border)
    "#86e2ec", // Info/300
    "#35cedf", // Info/400
    "#2aa5b2", // Info/500 → main
    "#207c86", // Info/600
    "#155259", // Info/700 → dark   (pill text)
    "#0b292d", // Info/800
    "#051516", // Info/900 → darker
  ];
  const successColors = [
    "#f6ffed", // Success/50  → lighter
    "#d9f7be", // Success/100
    "#b7eb8f", // Success/200 → light
    "#95de64", // Success/300
    "#73d13d", // Success/400
    "#52c41a", // Success/500 → main
    "#389e0d", // Success/600
    "#237804", // Success/700 → dark
    "#135200", // Success/800
    "#092b00", // Success/900 → darker
  ];

  // Mode-independent primary: Mantis docs show the brand uses one primary across
  // light/dark modes; only surfaces and text colors swap.

  return {
    primary: {
      50: primaryColors[0],
      lighter: primaryColors[1],
      100: primaryColors[2],
      200: primaryColors[3],
      light: primaryColors[4],
      400: primaryColors[5],
      main: primaryColors[6],
      dark: primaryColors[7],
      700: primaryColors[8],
      800: primaryColors[9],
      darker: primaryColors[10],
      900: primaryColors[11],
      contrastText,
    },
    secondary: {
      lighter: greyColors[100],
      100: greyColors[100],
      200: greyColors[200],
      light: greyColors[300],
      400: greyColors[400],
      main: greyColors[500]!,
      600: greyColors[600],
      dark: greyColors[700],
      800: greyColors[800],
      darker: greyColors[900],
      A100: greyColors[0],
      A200: greyColors.A400,
      A300: greyColors.A700,
      contrastText: greyColors[0],
    },
    error: {
      lighter: errorColors[0],
      light: errorColors[1],
      main: errorColors[2],
      dark: errorColors[3],
      darker: errorColors[4],
      contrastText,
    },
    warning: {
      lighter: warningColors[0],
      light: warningColors[1],
      main: warningColors[2],
      dark: warningColors[3],
      darker: warningColors[4],
      contrastText: greyColors[100],
    },
    info: {
      lighter: infoColors[0],
      100: infoColors[1],
      light: infoColors[2],
      300: infoColors[3],
      400: infoColors[4],
      main: infoColors[5],
      600: infoColors[6],
      dark: infoColors[7],
      darker: infoColors[8],
      900: infoColors[9],
      contrastText,
    },
    success: {
      lighter: successColors[0],
      100: successColors[1],
      light: successColors[2],
      300: successColors[3],
      400: successColors[4],
      main: successColors[5],
      600: successColors[6],
      dark: successColors[7],
      darker: successColors[8],
      900: successColors[9],
      contrastText,
    },
    grey: greyColors,
  };
}
